import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { getProducts } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

export const ShopSection = () => {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";

  useEffect(() => {
    getProducts()
      .then(setProducts)
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...new Set(products.map((p) => p.category))];

  useEffect(() => {
    const cat = searchParams.get("cat");
    if (cat && categories.includes(cat)) setCategory(cat);
  }, [searchParams, products]);

  const needle = q.trim().toLowerCase();
  const visible = products.filter((p) => {
    if (category !== "All" && p.category !== category) return false;
    if (needle && !`${p.name} ${p.category} ${p.tagline || ""} ${p.description || ""}`.toLowerCase().includes(needle)) return false;
    return true;
  });

  const clearSearch = () => {
    searchParams.delete("q");
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <section id="shop" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-4">01 · The Classina Range</p>
          <h2 className="font-serif text-3xl md:text-5xl font-medium tracking-tight leading-tight">
            The complete Classina range. <span className="italic text-primary">Ready to ship.</span>
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Notebooks, journals, sketch pads and filing — every product manufactured under one roof,
            benchmarked on quality, paper after paper.
          </p>
        </motion.div>

        {q && (
          <div data-testid="search-results-banner" className="mt-10 flex items-center gap-3 bg-secondary/50 border border-border/60 rounded-full px-5 py-3 w-fit">
            <p className="text-sm">
              Results for <span className="font-bold">“{q}”</span> — {visible.length} found
            </p>
            <button
              data-testid="search-clear-button"
              onClick={clearSearch}
              className="p-1 rounded-full hover:bg-secondary transition-colors"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="mt-12 flex flex-wrap gap-3" data-testid="category-filters">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.1em] transition-all duration-300 ${
                category === c
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-xl bg-muted/50 animate-pulse" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div data-testid="search-empty" className="mt-16 text-center py-16 bg-secondary/30 rounded-2xl">
            <p className="font-serif text-2xl">Nothing found{q ? ` for “${q}”` : ""}</p>
            <p className="text-sm text-muted-foreground mt-2">Try another word — or browse the full range below.</p>
            {q && (
              <button
                data-testid="search-empty-clear"
                onClick={clearSearch}
                className="mt-5 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div data-testid="product-grid" className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {visible.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
